/**
 * FeedbackScreenshotsGrid — the screenshots grid on the admin feedback details
 * page.
 *
 * Web port of Flutter's `_screenshotsGrid`
 * (`lib/presentation/screens/feedback/user_feedback_details_screen.dart:153-227`):
 * a `crossAxisCount: 3` grid where each cell renders a `ContentThumbnail` of a
 * screenshot and taps navigate to the full-viewport media viewer.
 *
 * Web deviations (directed by the task prompt, see the page JSDoc):
 * - Rendered via the shared {@link VirtualList} in grid mode — the same
 *   virtualized grid `UserDetailsPage` uses (through `FlexibleHeaderList`).
 *   The column count is dynamic on web (`useGridColumnsCount` — 3 portrait /
 *   5 landscape) rather than Flutter's fixed `crossAxisCount: 3`, and the
 *   shared `CONTENT_CONSTANTS.gridLayout` spacing/aspect tokens are reused so
 *   the feedback grid matches every other media grid in the app.
 * - Each cell is a square (`gridLayout.itemAspectRatio`) holding the screenshot
 *   centered at its own natural aspect ratio (`media.aspectRatio`) — exactly how
 *   `ContentGridItem` wraps `ContentPreview`: landscape media is shorter than the
 *   cell (centered, surface above/below), portrait overflows top/bottom and is
 *   clipped. Flutter instead stretches a `childAspectRatio: 3/4` cover thumbnail.
 * - The screenshot tap navigates to `DefaultMediaPage`
 *   (`Navigator.navigateToDefaultMedia`) instead of Flutter's content-list
 *   screen.
 *
 * For each screenshot a local `UserMediaEntity` is built mirroring Flutter's
 * `UserMedia` (`isContent: false`, `relativePath`/`relativeThumbnailPath =
 * screenshot.path`, `type: 0`, `aspectRatio: screenshot.aspectRatio ??
 * VIDEO_CONSTANTS.DEFAULT_ASPECT_RATIO`). The thumbnail URL is resolved via
 * `MediaUtils.getMediaThumbnailUrl`, which — for non-content, non-local media —
 * returns `env.DEFAULT_BUCKET_PREFIX + screenshot.path`, exactly the
 * "default bucket + screenshot.path" URL the screenshots are stored under.
 */
import { useCallback } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useGridColumnsCount } from '@/presentation/hooks';
import { Navigator } from '@/presentation/routing/Navigator';
import { VirtualList } from '@/presentation/components/common/virtualList/VirtualList';
import { ContentPreview } from '@/presentation/feature/content/components/ContentPreview';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { VIDEO_CONSTANTS } from '@/domain/constants/videoConstants';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import type { InAppFeedbackScreenshotEntity } from '@/domain/entities/feedback/inAppFeedbackEntity';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface FeedbackScreenshotsGridProps {
    screenshots: InAppFeedbackScreenshotEntity[];
}

/**
 * Build the minimal `UserMediaEntity` used to resolve a screenshot's thumbnail
 * URL. Mirrors Flutter's `UserMedia` construction at
 * `user_feedback_details_screen.dart:183-192`. Fields beyond the ones Flutter
 * sets are filled with inert defaults — `getMediaThumbnailUrl` only reads
 * `relativeThumbnailPath`, `isLocal`, and `isContent`.
 */
const buildScreenshotMedia = (
    screenshot: InAppFeedbackScreenshotEntity,
): UserMediaEntity => ({
    id: '',
    firebaseId: '',
    createdTimestamp: Date.now(),
    updatedTimestamp: 0,
    isProcessing: false,
    type: 0,
    relativePath: screenshot.path,
    relativeThumbnailPath: screenshot.path,
    aspectRatio: screenshot.aspectRatio ?? VIDEO_CONSTANTS.DEFAULT_ASPECT_RATIO,
    isDraft: false,
    allowComments: false,
    showCoins: false,
    shareLink: '',
    reports: 0,
    blocked: false,
    isStartUp: false,
    isLocal: false,
    isContent: false,
    allowUnlock: false,
    allowEarlyAccess: false,
});

export const FeedbackScreenshotsGrid = ({
    screenshots,
}: FeedbackScreenshotsGridProps) => {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();
    // Dynamic column count — 3 portrait / 5 landscape, the same responsive grid
    // sizing UserDetailsPage uses.
    const gridColumnsCount = useGridColumnsCount();

    const renderScreenshotItem = useCallback(
        (screenshot: InAppFeedbackScreenshotEntity, index: number) => {
            const media = buildScreenshotMedia(screenshot);
            const thumbnailUrl = MediaUtils.getMediaThumbnailUrl(media);

            return (
                <button
                    {...qaAttr({ id: `feedback-screenshot-${index}` })}
                    onClick={() =>
                        Navigator.navigateToDefaultMedia(
                            thumbnailUrl,
                            media.aspectRatio,
                        )
                    }
                    className="cursor-pointer overflow-hidden relative flex flex-col items-center justify-center"
                    style={{
                        width: '100%',
                        height: '100%',
                        padding: 0,
                        border: 'none',
                        background: theme.surface,
                        cursor: 'pointer',
                    }}
                >
                    {/* Inner box at the screenshot's natural aspect ratio,
                        centered in the square cell — mirrors how ContentGridItem
                        wraps ContentPreview. Landscape media is shorter than the
                        cell (centered, surface above/below); portrait overflows
                        top/bottom and is clipped by overflow-hidden. */}
                    <div style={{ width: '100%', aspectRatio: media.aspectRatio }}>
                        <ContentPreview
                            thumbnailUrl={thumbnailUrl}
                            qa={{ id: `feedback-screenshot-thumbnail-${index}` }}
                        />
                    </div>
                </button>
            );
        },
        [theme.surface],
    );

    return (
        <div
            style={{
                margin: `0 ${responsive.spacing.base}px`,
                background: theme.surface,
                borderRadius: responsive.borderRadius.base,
                padding: responsive.spacing.xxs,
            }}
        >
            <VirtualList<InAppFeedbackScreenshotEntity>
                items={screenshots}
                renderItem={renderScreenshotItem}
                getItemKey={(screenshot, index) => `${screenshot.path}-${index}`}
                gridProps={{
                    columnsCount: gridColumnsCount,
                    crossAxisSpacing: CONTENT_CONSTANTS.gridLayout.crossAxisSpacing,
                    mainAxisSpacing: CONTENT_CONSTANTS.gridLayout.mainAxisSpacing,
                    itemAspectRatio: CONTENT_CONSTANTS.gridLayout.itemAspectRatio,
                    bufferRows: CONTENT_CONSTANTS.gridLayout.bufferRows,
                }}
                backgroundColor={theme.surface}
                qa={{ id: 'feedback-screenshots-grid' }}
            />
        </div>
    );
};
